import { BountySortBy } from '@apeiron/bounty'
import {
  MayNull,
  PageInfoResponse,
  PageInput,
  serializePagingInput
} from '@apeiron/temp'
import { useLazyQuery } from '@apollo/client'
import graphql from '@src/graphql/query/getBounties'
import {
  BountyFilterInput,
  BountyNode,
  BountySortInput
} from '@src/types/bounty'
import { useCallback, useState } from 'react'

const useGetBounties = (): ReturnType => {
  const [query, { loading }] = useLazyQuery<GraphqlResponse>(graphql)

  const [data, setData] = useState<FetchBountiesResponse | null>(null)

  const fetch = useCallback(
    async (request: Request): Promise<MayNull<FetchBountiesResponse>> => {
      const {
        filterInput = {},
        sortInput = { sortBy: BountySortBy.PriceDesc },
        pageInput = {}
      } = request

      const response = await query({
        variables: {
          filterInput,
          sortInput,
          pagingInput: serializePagingInput(pageInput)
        }
      })

      if (response.data) {
        const returnData = {
          items: response.data.getBounties.edges.map(edge => edge.node),
          pageInfo: response.data.getBounties.pageInfo
        }

        setData(returnData)

        return returnData
      }

      return null
    },
    [query]
  )

  return {
    fetch,
    data,
    loading
  }
}

type Request = {
  filterInput: BountyFilterInput
  sortInput?: BountySortInput
  pageInput: PageInput
}

export type FetchBountiesResponse = {
  items: BountyNode[]
  pageInfo: PageInfoResponse
}

type GraphqlResponse = {
  getBounties: {
    edges: {
      node: BountyNode
    }[]
    pageInfo: PageInfoResponse
  }
}

type ReturnType = {
  fetch: (request: Request) => Promise<MayNull<FetchBountiesResponse>>
  data: MayNull<{
    items: BountyNode[]
    pageInfo: PageInfoResponse
  }>
  loading: boolean
}

export default useGetBounties
